import * as React from 'react';
import clsx from 'clsx';
import { useControllable } from '../../hooks';
import { makeId } from '../../utils';
import { Base } from '../Base';
import { SrOnly } from '../Helper';
import { PillSwitchContext, usePillSwitchProvider } from './PillSwitchContext';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var styles = {
  "root": "PillSwitch-root_EXVeB",
  "pill": "PillSwitch-pill_2fl_U",
  "indicator": "PillSwitch-indicator_3wvRW",
  "input": "PillSwitch-input_1B_yv"
};
const defaultIndicatorStyle = {};
function Root({
  name,
  defaultValue,
  value: valueProp,
  children,
  className,
  onValueChange,
  ...extraProps
}) {
  const rootRef = React.useRef(null);
  const indicatorRef = React.useRef(null);
  const [indicatorStyle, setIndicatorStyle] = React.useState(defaultIndicatorStyle);
  const [value, setValue] = useControllable({
    prop: valueProp,
    defaultProp: defaultValue,
    onChange: onValueChange
  });
  const context = React.useMemo(() => ({
    name,
    value,
    onValueChange: setValue
  }), [name, value, setValue]);
  React.useEffect(() => {
    const pills = rootRef.current?.children;
    if (!pills) return;
    const checked = Array.from(pills).find(elt => elt.matches('input:checked'));
    const rootMeta = rootRef.current?.getBoundingClientRect();
    const pillMeta = checked?.nextElementSibling?.getBoundingClientRect();
    if (!rootMeta || !pillMeta) return;
    setIndicatorStyle({
      left: pillMeta.left - rootMeta.left,
      width: pillMeta.width
    });
  }, [value]);
  return /*#__PURE__*/_jsxs(Base, {
    innerRef: rootRef,
    boxSizing: "border",
    position: "relative",
    flexInline: true,
    flexAlignItemsCenter: true,
    gap: "xxsmall",
    padding: "xxsmall",
    backgroundColor: "white",
    className: clsx(styles.root, className),
    ...extraProps,
    children: [/*#__PURE__*/_jsx(PillSwitchContext.Provider, {
      value: context,
      children: children
    }), /*#__PURE__*/_jsx(Base, {
      innerRef: indicatorRef,
      className: styles.indicator,
      style: indicatorStyle
    })]
  });
}
function Pill({
  id,
  name,
  value,
  checked: checkedProp,
  defaultChecked,
  inputProps,
  className,
  onChange,
  ...extraProps
}) {
  const genId = React.useId();
  const inputId = makeId('pill', id || genId);
  const context = usePillSwitchProvider();
  const checked = (() => {
    if (typeof defaultChecked === 'boolean') {
      return undefined;
    }
    if (typeof checkedProp === 'boolean') {
      return checkedProp;
    }
    if (typeof context?.value === 'string') {
      return context.value === value;
    }
    return false;
  })();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SrOnly, {
      children: /*#__PURE__*/_jsx("input", {
        ...inputProps,
        id: inputId,
        type: "radio",
        name: context?.name || name,
        value: value,
        defaultChecked: defaultChecked,
        checked: checked,
        className: styles.input,
        onChange: evt => {
          context?.onValueChange?.(value);
          onChange?.(evt);
        }
      })
    }), /*#__PURE__*/_jsx(Base, {
      tagName: "label",
      htmlFor: inputId,
      boxSizing: "border",
      flex: true,
      flexAlignItemsCenter: true,
      weight: "regular",
      className: clsx(styles.pill, className),
      ...extraProps
    })]
  });
}
Root.displayName = 'PillSwitch';
Pill.displayName = 'PillSwitchPill';
export { Root as PillSwitch, Pill as PillSwitchPill };